import {
  EDITOR_COPY,
  MCP_CALLBACK_PORT,
  MCP_CLIENT_ID,
  MCP_SERVER_NAME,
} from './copy';

/** The command that registers this app's MCP server in Claude Code (spec 236). */
export function claudeCodeCommand(address: string): string {
  return [
    'claude mcp add',
    '--transport http',
    // User scope: Claude Code's default (local) ties the server to the directory
    // the command ran in (its docs, "MCP installation scopes"), and a developer
    // then cannot find it in Claude Code anywhere else.
    '--scope user',
    `--client-id ${MCP_CLIENT_ID}`,
    `--callback-port ${MCP_CALLBACK_PORT}`,
    MCP_SERVER_NAME,
    address,
  ].join(' ');
}

/**
 * The AI tools the page has a setup for (spec 241), in the order of its tabs.
 * The address (`?tool=`) names one; Claude Code is the one shown otherwise.
 */
export const AI_TOOLS = [
  'claude-code',
  'copilot',
  'codex',
  'claude-ai',
  'other',
] as const;
export type AiTool = (typeof AI_TOOLS)[number];

/**
 * Whether claude.ai can reach this address. It connects from Anthropic's
 * servers, so a plain-HTTP or loopback address can never work for it; any
 * other one might, if the environment is public — which the page cannot see.
 */
export function reachableFromClaudeAi(address: string): boolean {
  const url = new URL(address);
  return (
    url.protocol === 'https:' &&
    !['localhost', '127.0.0.1', '[::1]'].includes(url.hostname)
  );
}

/**
 * The entry GitHub Copilot reads from `~/.copilot/mcp-config.json`: the Copilot
 * CLI's own file, which Copilot in VS Code reads too, so one block serves both.
 * The client id spares the sign-in a registration step our realm refuses.
 */
export function copilotConfig(address: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        [MCP_SERVER_NAME]: {
          type: 'http',
          url: address,
          tools: ['*'],
          oauthClientId: MCP_CLIENT_ID,
        },
      },
    },
    null,
    2,
  );
}

/**
 * The link that opens VS Code's own install page for this server, client id
 * included: the one install link that carries it (research note
 * 2026-10-28-mcp-install-links §2). URL-encoded JSON, not base64. It writes
 * VS Code's `mcp.json`, not the Copilot CLI's file.
 */
export function vsCodeInstallLink(address: string, insiders = false): string {
  const server = {
    name: MCP_SERVER_NAME,
    type: 'http',
    url: address,
    oauth: { clientId: MCP_CLIENT_ID },
  };
  return `${insiders ? 'vscode-insiders' : 'vscode'}:mcp/install?${encodeURIComponent(JSON.stringify(server))}`;
}

/**
 * claude.ai's "Add custom connector" dialog with the name and address filled
 * in — for oneself, or for the whole organization (an Owner's settings). It has
 * no field for the client id: that is still entered by hand.
 */
export function claudeAiConnectorLink(
  address: string,
  forOrganization = false,
): string {
  const query = new URLSearchParams({
    modal: 'add-custom-connector',
    connectorName: MCP_SERVER_NAME,
    connectorUrl: address,
  });
  const place = forOrganization ? 'admin-settings' : 'customize';
  return `https://claude.ai/${place}/connectors?${query.toString()}`;
}

/** The command that registers this server in Codex and signs in at once. */
export function codexCommand(address: string): string {
  return [
    'codex mcp add',
    MCP_SERVER_NAME,
    `--url ${address}`,
    `--oauth-client-id ${MCP_CLIENT_ID}`,
  ].join(' ');
}

/** The config entry most MCP editors read (`mcpServers`), for this server. */
export function editorConfig(address: string): string {
  return JSON.stringify(
    { mcpServers: { [MCP_SERVER_NAME]: { type: 'http', url: address } } },
    null,
    2,
  );
}

/** Something to type into an AI tool once it is connected, and what it gets. */
export interface TryItem {
  text: string;
  what?: string;
}

/**
 * What to try first in each tool (spec 242). Claude Code names a server's
 * prompts `/server:prompt` and attaches a resource with `@server:uri` (service
 * specs 198, 200); VS Code's Copilot Chat names them `/server.prompt` (its MCP
 * docs). Codex and claude.ai document tools only: they are asked in words.
 */
export function tryItems(tool: AiTool): readonly TryItem[] {
  switch (tool) {
    case 'claude-code':
      return [
        {
          text: `/${MCP_SERVER_NAME}:whats_waiting`,
          what: EDITOR_COPY.whatsWaiting,
        },
        {
          text: `/${MCP_SERVER_NAME}:pick_up_task`,
          what: EDITOR_COPY.pickUpTask,
        },
        { text: `@${MCP_SERVER_NAME}:sdlc://`, what: EDITOR_COPY.mention },
      ];
    case 'copilot':
      return [
        {
          text: `/${MCP_SERVER_NAME}.whats_waiting`,
          what: EDITOR_COPY.whatsWaiting,
        },
        {
          text: `/${MCP_SERVER_NAME}.pick_up_task`,
          what: EDITOR_COPY.pickUpTask,
        },
      ];
    case 'codex':
    case 'claude-ai':
    case 'other':
      return EDITOR_COPY.asks.map((text) => ({ text }));
  }
}
